import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  ChevronLeft,
  ChevronRight,
  Upload,
  FileText,
  CheckCircle,
  AlertCircle,
  Loader2,
  X,
  User,
  GraduationCap,
  BookOpen,
  Music,
  Briefcase,
  FileUp,
  PenLine,
  Send,
  CreditCard
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 1, title: "Student Info", icon: User },
  { id: 2, title: "Study Program", icon: GraduationCap },
  { id: 3, title: "Academics", icon: BookOpen },
  { id: 4, title: "Music Background", icon: Music },
  { id: 5, title: "Work Experience", icon: Briefcase },
  { id: 6, title: "Documents", icon: FileUp },
  { id: 7, title: "Essay", icon: PenLine },
  { id: 8, title: "Review & Submit", icon: Send },
];

const EMPTY_FORM = {
  student: { full_name: "", age: "", dob: "", gender: "", address: "", city: "", state: "", country: "", contact_number: "", email: "" },
  study_program: { intended_program: "", annual_tuition_fees: "", institution_type: "", institution_name: "", affiliated_university: "" },
  academics: {
    tenth: { board: "", year: "", marks: "" },
    twelfth: { board: "", year: "", marks: "" },
    graduation: { degree: "", college: "", year: "", marks: "" },
    postgraduation: { degree: "", institution: "", year: "", marks: "" },
  },
  music_background: { discipline: "", current_status: "", duration: "" },
  work_experience: [{ organization: "", role: "", duration: "", description: "" }],
  uploaded_documents: {},
  essay: "",
};

const DOCUMENT_TYPES = [
  { key: "id_proof", label: "ID Proof (Aadhar / Passport)", required: true },
  { key: "age_proof", label: "Age Proof", required: true },
  { key: "tenth_marksheet", label: "10th Marksheet & Certificate", required: true },
  { key: "twelfth_marksheet", label: "12th Marksheet & Certificate", required: true },
  { key: "graduation_docs", label: "Graduation Documents", required: true },
  { key: "postgraduation_docs", label: "Post-Graduation Documents (if applicable)", required: false },
  { key: "music_certification", label: "Music Certification / Letter from Music School (Optional)", required: false },
  { key: "student_resume", label: "Student Resume (PDF)", required: false },
];

function deepClone(obj) { return JSON.parse(JSON.stringify(obj)); }

export default function StudentScholarshipForm() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState(deepClone(EMPTY_FORM));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [applicationId, setApplicationId] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isPaid, setIsPaid] = useState(false);
  const [paying, setPaying] = useState(false);
  const [forexRate, setForexRate] = useState(null);
  const autosaveTimer = useRef(null);

  const getINRAmount = (eurAmount) => {
    const rate = forexRate || 90;
    return Math.ceil(eurAmount * rate);
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/student-scholarship/retrieve`, { credentials: "include" });
        const data = await res.json();
        if (data.found && data.application) {
          const app = data.application;
          if (app.status === "submitted") setIsSubmitted(true);
          if (app.status === "paid") { setIsSubmitted(true); setIsPaid(true); }
          setApplicationId(app.application_id || "");
          if (app.form_data) {
            setFormData(prev => ({
              ...deepClone(EMPTY_FORM),
              ...app.form_data,
              academics: { ...deepClone(EMPTY_FORM).academics, ...(app.form_data.academics || {}) },
              work_experience: app.form_data.work_experience?.length ? app.form_data.work_experience : deepClone(EMPTY_FORM).work_experience,
            }));
          }
          if (app.current_step) setStep(app.current_step);
        }
      } catch (e) { console.error("Failed to load:", e); }
      finally { setLoading(false); }
      // Fetch live forex rate
      try {
        const fxRes = await fetch(`${API_URL}/api/forex/eur-inr`);
        if (fxRes.ok) { const fxData = await fxRes.json(); if (fxData.rate) setForexRate(fxData.rate); }
      } catch (e) { console.error("Forex error:", e); }
    })();
  }, []);

  const triggerAutosave = useCallback(() => {
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(async () => {
      try {
        setSaving(true);
        await fetch(`${API_URL}/api/student-scholarship/autosave`, {
          method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
          body: JSON.stringify({ form_data: formData, current_step: step }),
        });
      } catch (e) { console.error("Autosave failed:", e); }
      finally { setSaving(false); }
    }, 2000);
  }, [formData, step]);

  useEffect(() => {
    if (!loading && !isSubmitted) triggerAutosave();
    return () => { if (autosaveTimer.current) clearTimeout(autosaveTimer.current); };
  }, [formData, step, triggerAutosave, loading, isSubmitted]);

  const updateField = (section, field, value) => {
    setFormData(prev => ({ ...prev, [section]: { ...prev[section], [field]: value } }));
  };
  const updateNestedField = (section, subsection, field, value) => {
    setFormData(prev => ({ ...prev, [section]: { ...prev[section], [subsection]: { ...prev[section][subsection], [field]: value } } }));
  };
  const updateWorkExp = (index, field, value) => {
    setFormData(prev => { const we = [...prev.work_experience]; we[index] = { ...we[index], [field]: value }; return { ...prev, work_experience: we }; });
  };
  const addWorkExp = () => {
    setFormData(prev => ({ ...prev, work_experience: [...prev.work_experience, { organization: "", role: "", duration: "", description: "" }] }));
  };
  const removeWorkExp = (index) => {
    setFormData(prev => ({ ...prev, work_experience: prev.work_experience.filter((_, i) => i !== index) }));
  };

  const handleFileUpload = async (docType, file) => {
    setUploading(prev => ({ ...prev, [docType]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("doc_type", docType);
      const res = await fetch(`${API_URL}/api/student-scholarship/upload`, { method: "POST", credentials: "include", body: fd });
      if (!res.ok) throw new Error("Upload failed");
      const data = await res.json();
      setFormData(prev => ({ ...prev, uploaded_documents: { ...prev.uploaded_documents, [docType]: data.filename } }));
      toast.success(`${docType.replace(/_/g, " ")} uploaded`);
    } catch (e) { toast.error("Upload failed: " + e.message); }
    finally { setUploading(prev => ({ ...prev, [docType]: false })); }
  };

  const handleDeleteUpload = async (docType) => {
    try {
      await fetch(`${API_URL}/api/student-scholarship/upload/${docType}`, { method: "DELETE", credentials: "include" });
      setFormData(prev => { const docs = { ...prev.uploaded_documents }; delete docs[docType]; return { ...prev, uploaded_documents: docs }; });
      toast.success("File removed");
    } catch (e) { toast.error("Failed to delete"); }
  };

  // Validation: Music Background (step 4) is now completely optional
  const validateStep = (s) => {
    const errors = [];
    if (s === 1) {
      const st = formData.student;
      if (!st.full_name) errors.push("Full Name is required");
      if (!st.age || parseInt(st.age) < 10) errors.push("Age must be 10 or above");
      if (!st.dob) errors.push("Date of Birth is required");
      if (!st.gender) errors.push("Gender is required");
      if (!st.address) errors.push("Address is required");
      if (!st.city) errors.push("City is required");
      if (!st.state) errors.push("State is required");
      if (!st.country) errors.push("Country is required");
      if (!st.contact_number) errors.push("Contact Number is required");
      if (!st.email) errors.push("Email is required");
    } else if (s === 2) {
      const sp = formData.study_program;
      if (!sp.intended_program) errors.push("Intended Program is required");
      if (!sp.annual_tuition_fees) errors.push("Annual Tuition Fees is required");
      if (!sp.institution_type) errors.push("Institution Type is required");
      if (!sp.institution_name) errors.push("Institution Name is required");
    } else if (s === 3) {
      const ac = formData.academics;
      if (!ac.tenth.board || !ac.tenth.year || !ac.tenth.marks) errors.push("10th Grade details are required");
      if (!ac.twelfth.board || !ac.twelfth.year || !ac.twelfth.marks) errors.push("12th Grade details are required");
    }
    // Step 4 (Music Background) - completely optional, no validation
    // Step 5 (Work Experience) - optional, no validation
    else if (s === 6) {
      const docs = formData.uploaded_documents || {};
      DOCUMENT_TYPES.forEach(dt => { if (dt.required && !docs[dt.key]) errors.push(`${dt.label} is required`); });
    } else if (s === 7) {
      if (!formData.essay || formData.essay.trim().split(/\s+/).filter(Boolean).length < 20) errors.push("Scholarship essay is required (minimum 20 words)");
    }
    return errors;
  };

  const goNext = () => {
    const errors = validateStep(step);
    if (errors.length > 0) { toast.error(errors[0]); return; }
    setStep(prev => Math.min(prev + 1, 8));
  };
  const goPrev = () => setStep(prev => Math.max(prev - 1, 1));

  const handleSubmit = async () => {
    // Validate steps 1-3 and 6-7 (skip 4 and 5 which are optional)
    const requiredSteps = [1, 2, 3, 6, 7];
    for (const s of requiredSteps) {
      const errors = validateStep(s);
      if (errors.length > 0) { toast.error(`Step ${s}: ${errors[0]}`); setStep(s); return; }
    }
    setSubmitting(true);
    try {
      // Save final data
      await fetch(`${API_URL}/api/student-scholarship/autosave`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ form_data: formData, current_step: 8 }),
      });
      // Submit
      const res = await fetch(`${API_URL}/api/student-scholarship/submit`, {
        method: "POST", credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Submission failed");
      setApplicationId(data.application_id);
      setIsSubmitted(true);
      toast.success("Application submitted successfully!");
    } catch (e) { toast.error("Error: " + e.message); }
    finally { setSubmitting(false); }
  };

  const handleScholarshipPayment = async () => {
    setPaying(true);
    try {
      const feeEUR = 100;
      const feeINR = getINRAmount(feeEUR);
      const amountInPaise = Math.round(feeINR * 100);

      const orderRes = await fetch(`${API_URL}/api/student-scholarship/create-payment`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ amount_inr: amountInPaise }),
      });
      if (!orderRes.ok) { const err = await orderRes.json(); throw new Error(err.detail || "Failed to create payment order"); }
      const orderData = await orderRes.json();

      const options = {
        key: orderData.key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: "Global Music Conservatory",
        description: "Academic Scholarship Application Fee (EUR 100)",
        order_id: orderData.order_id,
        theme: { color: "#182033" },
        handler: async function (response) {
          try {
            const verifyRes = await fetch(`${API_URL}/api/student-scholarship/verify-payment`, {
              method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              }),
            });
            if (!verifyRes.ok) { const err = await verifyRes.json(); throw new Error(err.detail || "Payment verification failed"); }
            setIsPaid(true);
            toast.success("Payment successful! Your scholarship application is now complete.");
          } catch (error) { toast.error("Payment verification failed: " + error.message); }
          finally { setPaying(false); }
        },
        modal: { ondismiss: () => setPaying(false) },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on("payment.failed", (response) => { toast.error("Payment failed: " + response.error.description); setPaying(false); });
      razorpay.open();
    } catch (error) { toast.error("Error: " + error.message); setPaying(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
        <span className="ml-3 text-lg text-slate-600">Loading application...</span>
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <div className="mx-auto max-w-2xl py-8 space-y-6">
        <div className="text-center space-y-4">
          <div className={`mx-auto flex h-20 w-20 items-center justify-center rounded-full ${isPaid ? "bg-green-100" : "bg-amber-100"}`}>
            {isPaid ? <CheckCircle className="h-10 w-10 text-green-600" /> : <GraduationCap className="h-10 w-10 text-amber-600" />}
          </div>
          <h1 className="text-2xl font-bold text-[#182033]">
            {isPaid ? "Application Complete!" : "Thank You for Applying!"}
          </h1>
          <p className="text-[#4b4a46] leading-relaxed">
            {isPaid
              ? <>Your scholarship application <span className="font-semibold">(Ref: {applicationId})</span> is complete. Fee paid successfully.</>
              : <>We have received your Academic Scholarship Application <span className="font-semibold">(Ref: {applicationId})</span>.</>
            }
          </p>
        </div>

        {isPaid ? (
          <Card className="border-green-300 bg-green-50">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="font-semibold text-green-800">Application Fee Paid - EUR 100</span>
              </div>
              <p className="text-sm text-green-800 leading-relaxed">
                The scholarship amount will be disbursed to your institution/college within <strong>90-120 days</strong>. When ready, you will receive an email requesting bank account details of your college/institution.
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-[#e1d2b3] bg-[#fdf8ef]">
            <CardContent className="p-6 space-y-4">
              <p className="text-sm text-[#4b4a46] leading-relaxed">
                We will let you know by email within <strong className="text-[#182033]">two weeks</strong> whether you are selected for the Scholarship or not.
              </p>

              {/* Pay Button Section */}
              <div className="rounded-xl bg-gradient-to-br from-[#182033] to-[#1e3a5f] p-5 text-center space-y-3">
                <Button
                  data-testid="pay-scholarship-form-btn"
                  onClick={handleScholarshipPayment}
                  disabled={paying}
                  className="bg-amber-500 hover:bg-amber-600 text-white font-semibold px-8 py-5 text-base"
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  {paying ? "Processing..." : "Pay Application Fees (EUR 100)"}
                </Button>
                <p className="text-xs text-slate-300 leading-relaxed">
                  If you have been <strong className="text-white">selected</strong> and received the scholarship decision email, pay the application fee here within <strong className="text-amber-300">10 days</strong> to complete the process. If not selected, there is no need to pay.
                </p>
              </div>

              <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 text-xs text-amber-800 leading-relaxed">
                <strong>No need to pay the application fees as of now.</strong> You can pay after results are communicated to you by email.
              </div>

              <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 space-y-2">
                <h3 className="font-semibold text-sm text-[#182033]">If You Are Selected:</h3>
                <ul className="text-xs text-[#4b4a46] space-y-2 leading-relaxed">
                  <li className="flex items-start gap-2"><CheckCircle className="h-3.5 w-3.5 text-amber-600 mt-0.5 shrink-0" /><span>You will have <strong>10 days</strong> to pay the <strong>EUR 100 application fee</strong> from your Student Portal Dashboard or this page.</span></li>
                  <li className="flex items-start gap-2"><CheckCircle className="h-3.5 w-3.5 text-amber-600 mt-0.5 shrink-0" /><span>Once paid, you will receive a <strong>fees payment confirmation email</strong>.</span></li>
                  <li className="flex items-start gap-2"><CheckCircle className="h-3.5 w-3.5 text-amber-600 mt-0.5 shrink-0" /><span>The scholarship amount will be <strong>disbursed to your institution/college in 90-120 days</strong>.</span></li>
                  <li className="flex items-start gap-2"><CheckCircle className="h-3.5 w-3.5 text-amber-600 mt-0.5 shrink-0" /><span>When ready, you will receive a mail requesting <strong>bank account details of your college/institution</strong>.</span></li>
                </ul>
              </div>

              <div className="rounded-lg bg-red-50 border border-red-200 p-4">
                <p className="text-xs text-red-800 leading-relaxed">
                  <strong>Important:</strong> If application fee is not paid within 10 days of the scholarship decision email, your application will be <strong>void</strong> and you can reapply in the next quarter.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="text-center">
          <Button onClick={() => navigate("/dashboard")} className="bg-[#182033] text-white hover:bg-[#1e293b]">
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const wordCount = formData.essay ? formData.essay.trim().split(/\s+/).filter(Boolean).length : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <h1 className="text-2xl font-bold text-[#182033] lg:text-3xl">Academic Scholarship Application</h1>
        <p className="text-sm text-slate-500">Student Portal Application</p>
        {saving && <span className="inline-flex items-center gap-1.5 text-xs text-amber-600"><Loader2 className="h-3 w-3 animate-spin" /> Saving...</span>}
      </div>

      {/* Progress Bar */}
      <div className="overflow-x-auto pb-2">
        <div className="flex items-center gap-1 min-w-[700px] px-2">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const isActive = step === s.id;
            const isComplete = step > s.id;
            return (
              <React.Fragment key={s.id}>
                <button onClick={() => { if (s.id < step) setStep(s.id); }} data-testid={`student-step-${s.id}-btn`}
                  className={`flex flex-col items-center gap-1 transition-all ${isActive ? "scale-105" : ""}`}>
                  <div className={`flex h-9 w-9 items-center justify-center rounded-full border-2 transition-colors ${
                    isActive ? "border-amber-500 bg-amber-500 text-white" : isComplete ? "border-green-500 bg-green-500 text-white" : "border-slate-300 bg-white text-slate-400"
                  }`}>{isComplete ? <CheckCircle className="h-4 w-4" /> : <Icon className="h-4 w-4" />}</div>
                  <span className={`text-[10px] whitespace-nowrap ${isActive ? "font-semibold text-amber-700" : isComplete ? "text-green-700" : "text-slate-400"}`}>{s.title}</span>
                </button>
                {i < STEPS.length - 1 && <div className={`h-0.5 flex-1 min-w-[20px] ${step > s.id ? "bg-green-400" : "bg-slate-200"}`} />}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Form */}
      <Card className="border-[#e1d2b3]">
        <CardContent className="p-6">

          {/* Step 1: Student Details */}
          {step === 1 && (
            <div className="space-y-5" data-testid="student-step-1-form">
              <h2 className="text-lg font-semibold text-[#182033] flex items-center gap-2"><User className="h-5 w-5 text-amber-600" /> Student Information</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField label="Full Name *" value={formData.student.full_name} onChange={v => updateField("student", "full_name", v)} />
                <FormField label="Age * (Minimum: 10 years)" type="number" value={formData.student.age} onChange={v => updateField("student", "age", v)} min="10" />
                <FormField label="Date of Birth *" type="date" value={formData.student.dob} onChange={v => updateField("student", "dob", v)} />
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-[#182033]">Gender *</Label>
                  <Select value={formData.student.gender} onValueChange={v => updateField("student", "gender", v)}>
                    <SelectTrigger className="border-[#d5c39d]"><SelectValue placeholder="Select gender" /></SelectTrigger>
                    <SelectContent><SelectItem value="Male">Male</SelectItem><SelectItem value="Female">Female</SelectItem><SelectItem value="Other">Other</SelectItem></SelectContent>
                  </Select>
                </div>
                <FormField label="Address *" value={formData.student.address} onChange={v => updateField("student", "address", v)} />
                <FormField label="City *" value={formData.student.city} onChange={v => updateField("student", "city", v)} />
                <FormField label="State *" value={formData.student.state} onChange={v => updateField("student", "state", v)} />
                <FormField label="Country *" value={formData.student.country} onChange={v => updateField("student", "country", v)} />
                <FormField label="Contact Number *" value={formData.student.contact_number} onChange={v => updateField("student", "contact_number", v)} />
                <FormField label="Email ID *" type="email" value={formData.student.email} onChange={v => updateField("student", "email", v)} />
              </div>
            </div>
          )}

          {/* Step 2: Study Program */}
          {step === 2 && (
            <div className="space-y-5" data-testid="student-step-2-form">
              <h2 className="text-lg font-semibold text-[#182033] flex items-center gap-2"><GraduationCap className="h-5 w-5 text-amber-600" /> Study Program Details</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField label="Intended Program of Study *" value={formData.study_program.intended_program} onChange={v => updateField("study_program", "intended_program", v)} placeholder="e.g., Bachelor of Arts, Engineering" />
                <FormField label="Annual Tuition Fees (in EUR) *" type="number" value={formData.study_program.annual_tuition_fees} onChange={v => updateField("study_program", "annual_tuition_fees", v)} />
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-[#182033]">Institution Type *</Label>
                  <Select value={formData.study_program.institution_type} onValueChange={v => updateField("study_program", "institution_type", v)}>
                    <SelectTrigger className="border-[#d5c39d]"><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent><SelectItem value="School">School</SelectItem><SelectItem value="College">College</SelectItem><SelectItem value="University">University</SelectItem></SelectContent>
                  </Select>
                </div>
                <FormField label="Name of Institution *" value={formData.study_program.institution_name} onChange={v => updateField("study_program", "institution_name", v)} />
                <FormField label="Affiliated University Name" value={formData.study_program.affiliated_university} onChange={v => updateField("study_program", "affiliated_university", v)} placeholder="Or direct University name" className="sm:col-span-2" />
              </div>
            </div>
          )}

          {/* Step 3: Academic Details */}
          {step === 3 && (
            <div className="space-y-5" data-testid="student-step-3-form">
              <h2 className="text-lg font-semibold text-[#182033] flex items-center gap-2"><BookOpen className="h-5 w-5 text-amber-600" /> Academic Details</h2>
              {[
                { key: "tenth", label: "10th Grade", fields: ["board", "year", "marks"], required: true },
                { key: "twelfth", label: "12th Grade", fields: ["board", "year", "marks"], required: true },
                { key: "graduation", label: "Graduation", fields: ["degree", "college", "year", "marks"], required: false },
                { key: "postgraduation", label: "Post-Graduation (if applicable)", fields: ["degree", "institution", "year", "marks"], required: false },
              ].map(level => (
                <div key={level.key} className="rounded-lg border border-slate-200 p-4 space-y-3">
                  <h3 className="font-semibold text-[#182033] text-sm">{level.label} {level.required && <span className="text-red-500">*</span>}</h3>
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    {level.fields.map(f => (
                      <FormField key={f} label={f === "marks" ? "Marks / GPA / %" : f.charAt(0).toUpperCase() + f.slice(1)}
                        value={formData.academics[level.key][f]} onChange={v => updateNestedField("academics", level.key, f, v)}
                        type={f === "year" ? "number" : "text"} placeholder={f === "year" ? "e.g., 2020" : ""} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Step 4: Music Background (Optional) */}
          {step === 4 && (
            <div className="space-y-5" data-testid="student-step-4-form">
              <h2 className="text-lg font-semibold text-[#182033] flex items-center gap-2"><Music className="h-5 w-5 text-amber-600" /> Music Background <span className="text-xs text-slate-400 font-normal">(Optional)</span></h2>
              <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-xs text-blue-800 mb-4">
                <strong>Note:</strong> This section is optional. Fill it only if you have a music background you'd like to highlight.
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-[#182033]">Music Discipline</Label>
                  <Select value={formData.music_background.discipline} onValueChange={v => updateField("music_background", "discipline", v)}>
                    <SelectTrigger className="border-[#d5c39d]"><SelectValue placeholder="Select discipline (optional)" /></SelectTrigger>
                    <SelectContent><SelectItem value="Instrument">Instrument</SelectItem><SelectItem value="Vocal">Vocal</SelectItem><SelectItem value="Performing Arts">Performing Arts</SelectItem><SelectItem value="None">None</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-[#182033]">Current Status</Label>
                  <Select value={formData.music_background.current_status} onValueChange={v => updateField("music_background", "current_status", v)}>
                    <SelectTrigger className="border-[#d5c39d]"><SelectValue placeholder="Select status (optional)" /></SelectTrigger>
                    <SelectContent><SelectItem value="Learning">Learning</SelectItem><SelectItem value="Completed">Completed</SelectItem><SelectItem value="N/A">N/A</SelectItem></SelectContent>
                  </Select>
                </div>
                <FormField label="Duration of Learning" value={formData.music_background.duration} onChange={v => updateField("music_background", "duration", v)} placeholder="e.g., 3 years (optional)" className="sm:col-span-2" />
              </div>
            </div>
          )}

          {/* Step 5: Work Experience (Optional) */}
          {step === 5 && (
            <div className="space-y-5" data-testid="student-step-5-form">
              <h2 className="text-lg font-semibold text-[#182033] flex items-center gap-2"><Briefcase className="h-5 w-5 text-amber-600" /> Work Experience <span className="text-xs text-slate-400 font-normal">(Optional)</span></h2>
              {formData.work_experience.map((we, i) => (
                <div key={i} className="rounded-lg border border-slate-200 p-4 space-y-3 relative">
                  {formData.work_experience.length > 1 && <button onClick={() => removeWorkExp(i)} className="absolute top-2 right-2 text-slate-400 hover:text-red-500"><X className="h-4 w-4" /></button>}
                  <div className="grid gap-3 sm:grid-cols-2">
                    <FormField label="Organization Name" value={we.organization} onChange={v => updateWorkExp(i, "organization", v)} />
                    <FormField label="Role" value={we.role} onChange={v => updateWorkExp(i, "role", v)} />
                    <FormField label="Duration" value={we.duration} onChange={v => updateWorkExp(i, "duration", v)} placeholder="e.g., 2 years" />
                    <FormField label="Description" value={we.description} onChange={v => updateWorkExp(i, "description", v)} />
                  </div>
                </div>
              ))}
              <Button type="button" variant="outline" onClick={addWorkExp} className="border-dashed border-slate-300 text-slate-500 text-sm">+ Add Another Experience</Button>
            </div>
          )}

          {/* Step 6: Document Uploads */}
          {step === 6 && (
            <div className="space-y-5" data-testid="student-step-6-form">
              <h2 className="text-lg font-semibold text-[#182033] flex items-center gap-2"><FileUp className="h-5 w-5 text-amber-600" /> Document Uploads</h2>
              <p className="text-xs text-slate-500">Upload PDF, JPG, or PNG files. All documents marked with * are mandatory.</p>
              <div className="space-y-3">
                {DOCUMENT_TYPES.map(dt => {
                  const uploaded = formData.uploaded_documents?.[dt.key];
                  const isUploading = uploading[dt.key];
                  return (
                    <div key={dt.key} className={`flex items-center justify-between rounded-lg border p-3 ${uploaded ? "border-green-300 bg-green-50" : dt.required ? "border-slate-200" : "border-dashed border-slate-200"}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        {uploaded ? <CheckCircle className="h-5 w-5 text-green-600 shrink-0" /> : <FileText className="h-5 w-5 text-slate-400 shrink-0" />}
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-[#182033] truncate">{dt.label} {dt.required && <span className="text-red-500">*</span>}</p>
                          {uploaded && <p className="text-xs text-green-700 truncate">{uploaded}</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 ml-3">
                        {uploaded && <button onClick={() => handleDeleteUpload(dt.key)} className="text-red-400 hover:text-red-600 text-xs">Remove</button>}
                        <label className={`cursor-pointer inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${isUploading ? "bg-slate-100 text-slate-400" : "bg-amber-500 text-white hover:bg-amber-600"}`}>
                          {isUploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                          {uploaded ? "Replace" : "Upload"}
                          <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" disabled={isUploading}
                            onChange={e => { if (e.target.files[0]) handleFileUpload(dt.key, e.target.files[0]); }} />
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 7: Scholarship Essay */}
          {step === 7 && (
            <div className="space-y-5" data-testid="student-step-7-form">
              <h2 className="text-lg font-semibold text-[#182033] flex items-center gap-2"><PenLine className="h-5 w-5 text-amber-600" /> Scholarship Essay</h2>
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
                <strong>Prompt:</strong> "Why does the student deserve this scholarship?"
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-[#182033]">Essay (500 words limit) *</Label>
                <textarea data-testid="student-essay-textarea"
                  className="w-full min-h-[250px] rounded-lg border border-[#d5c39d] p-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 resize-y"
                  placeholder="Write your essay here..." value={formData.essay}
                  onChange={e => { const words = e.target.value.trim().split(/\s+/).filter(Boolean); if (words.length <= 500 || e.target.value.length < formData.essay.length) setFormData(prev => ({ ...prev, essay: e.target.value })); }} />
                <div className="flex justify-between text-xs">
                  <span className={wordCount > 450 ? "text-amber-600" : "text-slate-400"}>{wordCount} / 500 words</span>
                  {wordCount >= 500 && <span className="text-red-500">Word limit reached</span>}
                </div>
              </div>
            </div>
          )}

          {/* Step 8: Review & Submit */}
          {step === 8 && (
            <div className="space-y-5" data-testid="student-step-8-review">
              <h2 className="text-lg font-semibold text-[#182033] flex items-center gap-2"><Send className="h-5 w-5 text-amber-600" /> Review & Submit</h2>

              <ReviewSection title="Student Information" data={[
                ["Full Name", formData.student.full_name], ["Age / DOB", `${formData.student.age} years / ${formData.student.dob}`],
                ["Gender", formData.student.gender], ["Address", `${formData.student.address}, ${formData.student.city}, ${formData.student.state}, ${formData.student.country}`],
                ["Contact", formData.student.contact_number], ["Email", formData.student.email],
              ]} onEdit={() => setStep(1)} />
              <ReviewSection title="Study Program" data={[
                ["Program", formData.study_program.intended_program], ["Tuition Fees", `EUR ${formData.study_program.annual_tuition_fees}`],
                ["Institution Type", formData.study_program.institution_type], ["Institution", formData.study_program.institution_name],
                ["Affiliated University", formData.study_program.affiliated_university || "N/A"],
              ]} onEdit={() => setStep(2)} />
              
              {/* Music Background - only show if filled */}
              {(formData.music_background.discipline || formData.music_background.current_status || formData.music_background.duration) && (
                <ReviewSection title="Music Background (Optional)" data={[
                  ["Discipline", formData.music_background.discipline || "N/A"], 
                  ["Status", formData.music_background.current_status || "N/A"], 
                  ["Duration", formData.music_background.duration || "N/A"],
                ]} onEdit={() => setStep(4)} />
              )}

              <div className="rounded-lg border border-slate-200 p-4 space-y-2">
                <div className="flex items-center justify-between"><h3 className="font-semibold text-sm text-[#182033]">Documents</h3><button onClick={() => setStep(6)} className="text-xs text-amber-600 hover:underline">Edit</button></div>
                <div className="grid gap-1 sm:grid-cols-2">
                  {DOCUMENT_TYPES.map(dt => (
                    <div key={dt.key} className="flex items-center gap-2 text-xs">
                      {formData.uploaded_documents?.[dt.key] ? <CheckCircle className="h-3.5 w-3.5 text-green-600" /> : <AlertCircle className={`h-3.5 w-3.5 ${dt.required ? "text-red-500" : "text-slate-300"}`} />}
                      <span className="text-slate-600">{dt.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-lg border border-slate-200 p-4 space-y-2">
                <div className="flex items-center justify-between"><h3 className="font-semibold text-sm text-[#182033]">Essay</h3><button onClick={() => setStep(7)} className="text-xs text-amber-600 hover:underline">Edit</button></div>
                <p className="text-xs text-slate-600 line-clamp-3">{formData.essay || "Not written yet"}</p>
                <p className="text-xs text-slate-400">{wordCount} words</p>
              </div>

              {/* Guidelines + Submit */}
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 space-y-2 text-xs text-[#4b4a46]">
                <h3 className="font-semibold text-sm text-[#182033]">Important Guidelines</h3>
                <ul className="space-y-1.5 leading-relaxed">
                  <li>After submission, we will review your application and notify you via email within <strong>two weeks</strong>.</li>
                  <li>If selected, you will have <strong>10 days</strong> to pay the <strong>EUR 100 application fee</strong> from your Student Portal Dashboard.</li>
                  <li>If not selected, there is <strong>no need to pay</strong> the application fee.</li>
                  <li>If fee is not paid within 10 days of the decision email, the application becomes <strong>void</strong>.</li>
                  <li>Scholarship disbursement takes <strong>90-120 days</strong> to your institution/college.</li>
                </ul>
              </div>

              <Button data-testid="student-submit-btn" onClick={handleSubmit} disabled={submitting}
                className="w-full bg-gradient-to-r from-[#182033] to-[#1e3a5f] text-white py-5 text-base font-semibold">
                {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting...</> : <><Send className="h-4 w-4 mr-2" /> Submit Scholarship Application</>}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button data-testid="student-prev-btn" variant="outline" onClick={goPrev} disabled={step === 1} className="border-[#d5c39d] text-[#182033]">
          <ChevronLeft className="h-4 w-4 mr-1" /> Previous
        </Button>
        <span className="text-sm text-slate-500">Step {step} of 8</span>
        {step < 8 ? (
          <Button data-testid="student-next-btn" onClick={goNext} className="bg-[#182033] text-white hover:bg-[#1e293b]">
            Next <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        ) : <div />}
      </div>
    </div>
  );
}

function FormField({ label, value, onChange, type = "text", placeholder = "", className = "", min, ...props }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <Label className="text-sm font-medium text-[#182033]">{label}</Label>
      <Input type={type} value={value || ""} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        className="border-[#d5c39d] focus:ring-amber-500" min={min} {...props} />
    </div>
  );
}

function ReviewSection({ title, data, onEdit }) {
  return (
    <div className="rounded-lg border border-slate-200 p-4 space-y-2">
      <div className="flex items-center justify-between"><h3 className="font-semibold text-sm text-[#182033]">{title}</h3><button onClick={onEdit} className="text-xs text-amber-600 hover:underline">Edit</button></div>
      <div className="grid gap-1 sm:grid-cols-2">
        {data.map(([label, value], i) => (<div key={i} className="text-xs"><span className="text-slate-400">{label}: </span><span className="text-[#182033] font-medium">{value || "N/A"}</span></div>))}
      </div>
    </div>
  );
}
